import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import HalfLayout from "components/HalfLayout";
import Map from "components/Map";

import MapActioner from "services/map/actioner";
import MapUtils from "services/map/utils";
import ContractUtils from "services/contract/utils";
import ParkStatisticsUtils from "services/parkStatistics/utils";
import ParkStatisticsActioner from "services/parkStatistics/actioner";

import ParkStatisticsLayout from "scenes/ParkStatistics/components/ParkStatisticsLayout";
import MapComponent from "scenes/ParkStatistics/components/MapComponent";

function ParkStatistics({
  currentFloorAreas,
  contract,
  currentFilters,
  currentFloor,
  parkStatistics,
  retrieveAllParkStats,
  setFilter,
  setCenterToDefault,
  parkedCars,
  availableParking,
}) {
  const [getAllStats, setGetAllStats] = useState(false);

  const initializeData = useCallback(() => {
    retrieveAllParkStats(getAllStats);
  }, [retrieveAllParkStats, getAllStats]);

  useEffect(() => {
    if (parkStatistics?.map?.initialized) {
      initializeData();
    }
  }, [
    currentFloor,
    currentFilters?.areaType,
    parkStatistics?.map?.initialized,
    parkStatistics?.map?.selectedLayers,
    initializeData,
  ]);

  const toggleMarkersAndAreas = useMemo(() => {
    return MapUtils.toggleMarkersAndAreas(
      currentFilters?.timestamp,
      ContractUtils.getParkMaxDaysAllowed(contract)
    );
  }, [currentFilters?.timestamp, contract]);

  const toggleSpaceLabels = useMemo(() => {
    return (
      parkedCars?.list?.items?.some(
        (car) => car?.meta_data?.client?.label !== undefined
      ) ||
      availableParking?.list?.items?.some(
        (spot) => spot?.meta_data?.client?.label !== undefined
      )
    );
  }, [parkedCars?.list?.items, availableParking?.list?.items]);

  const handleRecenter = useCallback(
    (mapRef) => {
      const selectedAreas = currentFloorAreas?.filter((area) => area.selected);
      if (!toggleMarkersAndAreas || !selectedAreas?.length) return null;

      const selectedAreasPolygons = selectedAreas.reduce((polygons, area) => {
        return [...polygons, ...(area?.polygon || [])];
      }, []);

      mapRef.fitBounds(selectedAreasPolygons, {
        padding: [50, 50],
      });
    },
    [currentFloorAreas, toggleMarkersAndAreas]
  );

  const handleTimestampChange = useCallback(
    (timestamp) => {
      setFilter("timestamp", timestamp);
      initializeData();
    },
    [initializeData, setFilter]
  );

  const handleReloadOnce = useCallback(() => {
    handleTimestampChange(new Date().getTime() / 1000);
  }, [handleTimestampChange]);

  const handleAllStatsChange = useCallback(
    (isChecked) => {
      setGetAllStats(isChecked);
      handleReloadOnce();
    },
    [handleReloadOnce]
  );

  return (
    <HalfLayout
      leftComponent={
        <ParkStatisticsLayout
          onTimestampChange={handleTimestampChange}
          onReloadOnce={handleReloadOnce}
          getAllStats={getAllStats}
        />
      }
      rightComponent={
        <Map
          daysHoursSlider
          floorsButton
          getSliderMaxDaysAllowed={
            ContractUtils.getMaxDaysAllowedHeatmapOccupancy
          }
          selectedTimestamp={currentFilters?.timestamp}
          onTimestampChange={handleTimestampChange}
          onRecenter={handleRecenter}
          mapComponent={<MapComponent onRecenter={setCenterToDefault} />}
          withCheckboxOptions={{
            aisles: toggleMarkersAndAreas,
            labels: toggleMarkersAndAreas,
            displays: false,
            spaceLabels: toggleSpaceLabels,
          }}
          onAllStatsChange={handleAllStatsChange}
          getAllStats={getAllStats}
        />
      }
    />
  );
}

const mapStateToProps = (state) => ({
  currentFilters: state.ParkStatistics.filters,
  currentFloor: state.Map?.config?.currentFloor,
  contract: state.Contract.contract,
  currentFloorAreas: ParkStatisticsUtils.formatAreasFromCurrentFloor(state),
  parkStatistics: state.ParkStatistics,
  parkedCars: state.ParkedCars,
  availableParking: state.AvailableParking,
});

const mapDispatchToProps = (dispatch) => ({
  setFilter: (key, value) =>
    dispatch(ParkStatisticsActioner.setFilter(key, value)),
  retrieveAllParkStats: (allAreas) =>
    dispatch(ParkStatisticsActioner.retrieveAllStats(allAreas)),
  setCenterToDefault: () => dispatch(MapActioner.setCenterToDefault()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ParkStatistics);
